// In src/pages/JoinChurchPage.js

import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import {
  Container, Typography, Box, Autocomplete, TextField, Button,
  CircularProgress, Alert, Paper
} from '@mui/material';

function JoinChurchPage() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();

  const [churchesList, setChurchesList] = useState([]);
  const [selectedChurch, setSelectedChurch] = useState(null);
  const [churchSearchInput, setChurchSearchInput] = useState('');
  const [fetchingChurches, setFetchingChurches] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch publicly discoverable churches for the search dropdown
  useEffect(() => {
    const fetchChurches = async () => {
      setFetchingChurches(true);
      try {
        const q = query(collection(db, 'churches'), where('isPubliclyDiscoverable', '==', true));
        const querySnapshot = await getDocs(q);
        setChurchesList(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        console.error("Error fetching churches:", err);
        setError("Failed to load the list of churches. Please try again later.");
      } finally {
        setFetchingChurches(false);
      }
    };
    fetchChurches();
  }, []);

  const handleSubmitRequest = async () => {
    if (!selectedChurch || !currentUser) {
      setError('Please select a church to join.');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      let newStatus = 'pending_approval';

      // If the selected church has auto-join enabled, set status to active immediately
      if (selectedChurch.autoJoinEnabled) {
        newStatus = 'active';
      }

      await updateDoc(userDocRef, {
        churchId: selectedChurch.id,
        status: newStatus
      });

      // If auto-joined, we need to set custom claims and navigate to the home page
      if (newStatus === 'active') {
        const idToken = await currentUser.getIdToken();
        await fetch('https://setcustomclaims-ltocsivqja-uc.a.run.app', { // Replace with your actual cloud function URL
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ idToken }),
        });
        await currentUser.getIdToken(true); // Force refresh claims on the client
        alert(`Successfully joined ${selectedChurch.name}!`);
        navigate('/');
      } else {
        setSuccess(`Your request to join ${selectedChurch.name} has been sent for approval.`);
        // Force a reload after a delay to ensure the user profile context updates
        // and the new pending approval message is shown in the layout.
        setTimeout(() => window.location.reload(), 3000);
      }
    } catch (err) {
      console.error("Error submitting join request:", err);
      setError(`Failed to submit request: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // If the user is already part of a church, they shouldn't be on this page.
  if (userProfile?.churchId) {
    return <Navigate to="/" />;
  }

  return (
    <Container maxWidth="sm">
      <Paper sx={{ p: {xs: 2, sm: 4}, mt: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Join a Church Community
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Search for your church below to send a request to join. Once your request is approved by an administrator, you will gain full access to the app's features.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Box>
          {fetchingChurches ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Autocomplete
              options={churchesList}
              getOptionLabel={(option) => `${option.name} (${option.city}, ${option.state})`}
              onChange={(event, newValue) => {
                setSelectedChurch(newValue);
              }}
              inputValue={churchSearchInput}
              onInputChange={(event, newInputValue) => {
                setChurchSearchInput(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search for your Church"
                  margin="normal"
                  fullWidth
                />
              )}
              sx={{ mb: 2 }}
            />
          )}

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleSubmitRequest}
            disabled={!selectedChurch || submitting || fetchingChurches}
            sx={{ mt: 2, py: 1.5 }}
          >
            {submitting ? <CircularProgress size={24} color="inherit" /> : `Request to Join ${selectedChurch ? selectedChurch.name : ''}`}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

export default JoinChurchPage;
